
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button } from "@/ui-components/ui/button";
import { Badge } from "@/ui-components/ui/badge";
import { PrimaryActionButton } from "@/util-components/page-layout";
import { EditTemplateDialog } from "../components";
import {
  Plus, 
  LayoutTemplate,
  Download,
  ShoppingBag,
  Copy,
  Zap,
  PenTool,
  CheckCircle,
  XCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Template } from "../core/template.schemas";
import { 
  useTemplates2FindAllStore, 
  useTemplates2FindAllInternalStore,
  useTemplates2ImportStore,
  useTemplates2DuplicateStore,
  useTemplates2GetStoreCategories,
  useTemplates2Update,
  useTemplates2BulkDeleteStore,
  useTemplates2BulkPublishStore
} from "@/lib/api/requests/templates.requests";
import { Switch } from '@/ui-components/ui/switch';
import { TemplatesFilterParams } from "@/lib/api/interfaces/templates.interface";
import { DeleteTemplateDialog } from "../components/DeleteTemplateDialog";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { TemplateInfoDialog } from "../components/TemplateInfoDialog";
import { ImportStoreTemplateDialog } from "../template-editor/components/ImportStoreTemplateDialog";
import { CreateTemplateDialog } from '../components/CreateTemplateDialog';
import { usePermissions } from "@/lib/hooks/use-permissions";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { toast } from "@/lib/toast";
import {
  DataView,
  DataViewToolbar,
  DataViewLayout,
  DataViewContent,
  DataViewLoadingState,
  DataViewEmptyState,
  DataViewLoadMore,
  DataViewGridCard,
  DataViewListCard,
  GenericItem,
  GenericItemActions,
  EntityPreviewFrame
} from "@/util-components/data-view";
import { PageInfoBanner } from "@/util-components/page-layout";
import { usePageState } from "@/lib/hooks/use-page-state";
import { useTemplatePermissions } from '../template-editor/hooks/useTemplatePermissions';
import { AdvancedFilterDef, AdvancedSortDef, AdvancedState } from "@/util-components/data-view/components/advanced-filter-types";
import {
  setOnboardingImportedTemplateId,
  useOnboardingTourStep,
} from "@/util-components/onboarding/tour-step-store";

const DEFAULT_PAGE_SIZE = 18;

const StoreTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { isInternal } = usePermissions();
  const { canManageStore: canManageTemplateStore} = useTemplatePermissions();
  const onboardingTourStep = useOnboardingTourStep();
  const isOnboardingImportFlow =
    onboardingTourStep === 'store-select' ||
    onboardingTourStep === 'store-customize' ||
    onboardingTourStep === 'store-import';
  
  const { state, setInfoBannerVisible, setViewMode, setSettings } = usePageState('post-generator-store-templates', {
    isInfoBannerVisible: true,
    viewMode: 'grid',
    settings: {
      advancedState: {
        filters: [],
        sorts: [{ key: 'importCount', direction: 'desc' as const }]
      } as AdvancedState
    }
  });

  const [searchTerm, setSearchTerm] = useState("");
  const advancedState: AdvancedState = useMemo(
    () => (state.settings?.advancedState as AdvancedState | undefined) || { filters: [], sorts: [{ key: 'importCount', direction: 'desc' as const }] },
    [state.settings?.advancedState]
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [allTemplates, setAllTemplates] = useState<Template[]>([]);
  const [lastFilterKey, setLastFilterKey] = useState<string>('');
  
  const viewMode = state.viewMode || 'grid';
  
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<Template | null>(null);

  // Queries
  const categoriesQuery = useTemplates2GetStoreCategories({ enabled: true });

  // Definitions
  const sortDefs: AdvancedSortDef[] = useMemo(() => {
    const defs: AdvancedSortDef[] = [
        { key: 'updated', label: 'Aktualisiert' },
        { key: 'created', label: 'Erstellt' },
        { key: 'importCount', label: 'Imports' }
    ];
    if (isInternal) {
        defs.push({ key: 'postCount', label: 'Verwendung' });
    }
    return defs;
  }, [isInternal]);

  const filterDefs: AdvancedFilterDef[] = useMemo(() => {
    const catOptions = (categoriesQuery.data?.categories || []).map(c => ({ label: c, value: c }));
    return [
        {
            key: 'category',
            label: 'Kategorie',
            type: 'multi-select',
            options: catOptions
        },
        {
            key: 'aspectRatio',
            label: 'Ausrichtung',
            type: 'multi-select',
            options: [
                { label: 'Quadratisch (1:1)', value: '1:1' },
                { label: 'Story (9:16)', value: '9:16' },
                { label: 'Landschaft (16:9)', value: '16:9' },
                { label: 'Instagram-Feed (4:5)', value: '4:5' }
            ]
        }
    ];
  }, [categoriesQuery.data]);

  const currentFilterKey = useMemo(() => {
    return `${searchTerm.trim()}-${JSON.stringify(advancedState)}`;
  }, [searchTerm, advancedState]);

  const filterParams: TemplatesFilterParams = useMemo(() => {
    const sort = advancedState.sorts[0] || { key: 'updated', direction: 'desc' };
    
    // Extract filters
    const categoryFilter = advancedState.filters.find(f => f.key === 'category')?.value || [];
    const aspectRatioFilter = advancedState.filters.find(f => f.key === 'aspectRatio')?.value || [];

    return {
      textSearch: searchTerm.trim() || undefined,
      categories: Array.isArray(categoryFilter) && categoryFilter.length > 0 ? categoryFilter : undefined,
      aspectRatios: Array.isArray(aspectRatioFilter) && aspectRatioFilter.length > 0 ? aspectRatioFilter : undefined,
      page: currentPage,
      limit: DEFAULT_PAGE_SIZE,
      sortBy: sort.key as any,
      direction: sort.direction
    };
  }, [searchTerm, advancedState, currentPage]);

  const customerQuery = useTemplates2FindAllStore(filterParams, { enabled: !isInternal });
  const internalQuery = useTemplates2FindAllInternalStore(filterParams, { enabled: isInternal });
  
  const activeQuery = isInternal ? internalQuery : customerQuery;
  const importMutation = useTemplates2ImportStore();
  const duplicateMutation = useTemplates2DuplicateStore();
  const updateMutation = useTemplates2Update();
  const bulkDeleteMutation = useTemplates2BulkDeleteStore();
  const bulkPublishMutation = useTemplates2BulkPublishStore();
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (currentFilterKey !== lastFilterKey) {
      setCurrentPage(1);
      setAllTemplates([]);
      setLastFilterKey(currentFilterKey);
    }
  }, [currentFilterKey, lastFilterKey]);

  useEffect(() => {
    if (activeQuery.data?.docs) {
      if (currentPage === 1) {
        setAllTemplates(activeQuery.data.docs);
      } else {
        setAllTemplates(prev => {
          const newDocs = activeQuery.data?.docs || [];
          const existingIds = new Set(prev.map(a => a.id));
          const filteredNewDocs = newDocs.filter((d: any) => !existingIds.has(d.id));
          return [...prev, ...filteredNewDocs];
        });
      }
    }
  }, [activeQuery.data, currentPage]);

  const hasMoreTemplates = activeQuery.data?.hasNextPage || false;
  
  // Prevent empty state flash by checking if we have data in query but not yet in state
  const isSyncing = (activeQuery.data?.docs?.length ?? 0) > 0 && allTemplates.length === 0;
  const isInitialLoading = (activeQuery.isLoading || isSyncing) && currentPage === 1;
  const isLoadingMore = activeQuery.isLoading && currentPage > 1;

  const handleLoadMore = () => {
    if (hasMoreTemplates && !isLoadingMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleViewModeChange = (newViewMode: 'grid' | 'list') => {
    setViewMode(newViewMode);
  };
  
  const handleAdvancedStateChange = (newState: AdvancedState) => {
    setSettings({ advancedState: newState });
  };

  const handleAdvancedReset = () => {
    setSettings({ 
        advancedState: { 
            filters: [], 
            sorts: [{ key: 'importCount', direction: 'desc' }] 
        } 
    });
    setSearchTerm("");
  };

  const handleCreateSuccess = async () => {
    await activeQuery.refetch();
    setCurrentPage(1);
  };

  const handleDeleteSuccess = async () => {
    await activeQuery.refetch();
    setCurrentPage(1);
    setAllTemplates(prev => prev.filter(t => t.id !== templateToDelete?.id));
    setShowDeleteDialog(false);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedIds.length > 0) {
      bulkDeleteMutation.mutate(selectedIds, {
        onSuccess: async () => {
          toast.success(`${selectedIds.length} Store-Templates erfolgreich gelöscht`);
          await activeQuery.refetch();
          setCurrentPage(1);
          setAllTemplates(prev => prev.filter(t => !selectedIds.includes(t.id)));
          setSelectedIds([]);
          setShowDeleteDialog(false);
          setIsBulkDelete(false);
        },
        onError: (error) => {
          toast.error("Templates konnten nicht gelöscht werden", { error });
        }
      });
    }
  };

  const handleSelectionDelete = () => {
    if (selectedIds.length > 0) {
      setTemplateToDelete(null);
      setIsBulkDelete(true);
      setShowDeleteDialog(true);
    }
  };

  const handleBulkPublish = (publish: boolean) => {
    if (selectedIds.length > 0) {
      bulkPublishMutation.mutate({ ids: selectedIds, publish }, {
        onSuccess: async () => {
          toast.success(`${selectedIds.length} Templates erfolgreich ${publish ? 'veröffentlicht' : 'versteckt'}`);
          await activeQuery.refetch();
          setCurrentPage(1);
          setSelectedIds([]);
        },
        onError: (error) => {
          toast.error(`Templates konnten nicht ${publish ? 'veröffentlicht' : 'versteckt'} werden`, { error });
        }
      });
    }
  };

  const handleEditSuccess = async () => {
    await activeQuery.refetch();
    setCurrentPage(1);
  };

  const handleDuplicate = async (templateId: string) => {
    try {
      await duplicateMutation.mutateAsync(templateId);
      toast.success("Template dupliziert", {
        description: "Das Store-Template wurde erfolgreich dupliziert.",
      });
      await activeQuery.refetch();
      setCurrentPage(1);
    } catch (error) {
       toast.error("Fehler beim Duplizieren", {
        description: "Das Template konnte nicht dupliziert werden.",
        error,
      });
    }
  };

  const handlePublishToggle = React.useCallback(async (templateId: string, nextStatus: boolean) => {
    try {
      const updated = await updateMutation.mutateAsync({
        id: templateId,
        payload: {
          meta: { isPublished: nextStatus }
        }
      });
      setAllTemplates(prev => prev.map(t => (t.id === templateId ? updated : t)));
      toast.success(nextStatus ? "Template veröffentlicht" : "Template versteckt", {
        description: nextStatus ? "Das Template ist jetzt für Kunden sichtbar." : "Das Template ist für Kunden nicht mehr sichtbar.",
      });
    } catch (error) {
       toast.error("Fehler beim Aktualisieren", {
        description: "Der Status konnte nicht geändert werden.",
        error,
      });
    }
  }, [updateMutation]);

  const openImportDialog = useCallback((template: Template | null) => {
    if (!template) {
      return;
    }

    setShowInfoDialog(false);
    setSelectedTemplate(template);
    setShowImportDialog(true);
  }, []);

  const handleImportDialogOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen && importMutation.isPending) {
      return;
    }

    setShowImportDialog(nextOpen);

    if (!nextOpen) {
      setSelectedTemplate(null);
    }
  }, [importMutation.isPending]);

  const handleImport = (templateId: string) => {
    const tpl = allTemplates.find(t => t.id === templateId) || null;
    openImportDialog(tpl);
  };

  const handleConfirmImport = async (overrides: Record<string, any>, thumbnailData?: string) => {
    if (!selectedTemplate) return;
    try {
      const importedTemplate = await importMutation.mutateAsync({ id: selectedTemplate.id, overrides, thumbnailData });

      if (isOnboardingImportFlow) {
        setOnboardingImportedTemplateId(importedTemplate.id);
        navigate("/post-generator/templates");
        return;
      }

      handleImportDialogOpenChange(false);
      toast.success("Template importiert", {
        description: "Das Template wurde erfolgreich an deinen Verein angepasst.",
      });
    } catch (error) {
      toast.error("Fehler beim Importieren", {
        description: "Das Template konnte nicht importiert werden.",
        error,
      });
    }
  };

  const getAspectRatioDisplay = (aspectRatio: Template['aspectRatio']) => {
    return aspectRatio;
  };

  const convertToGenericItem = React.useCallback((template: Template): GenericItem => {
    const importCount = template.usageStats?.importCount || 0;
    const postCount = template.usageStats?.postCount || 0;

    return {
      id: template.id,
      title: template.title,
      previewContent: (
        <EntityPreviewFrame backgroundUrl={template.meta?.thumbnailPath}>
          {template.meta?.thumbnailPath ? (
            <img 
              src={template.meta.thumbnailPath} 
              alt={template.title} 
              className="max-w-full max-h-full rounded-sm object-contain"
              loading="lazy"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-300">
              <LayoutTemplate className="h-12 w-12 mb-2" />
            </div>
          )}
        </EntityPreviewFrame>
      ),
      metadata: {
        badgeText: [
          <div key="stats" className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Download className="h-3.5 w-3.5 text-black-500" />
              <span className="text-[10px] font-bold text-gray-700">{importCount}</span>
            </div>
            {isInternal && (
              <div className="flex items-center gap-1 border-l border-gray-200 pl-2">
                <Zap className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-[10px] font-bold text-gray-700">{postCount}</span>
              </div>
            )}
          </div>,
          getAspectRatioDisplay(template.aspectRatio),
        ],
        categories: template.meta?.categories,
        status: canManageTemplateStore ? (
          <div className="flex items-center gap-2 min-w-0" onClick={(e) => e.stopPropagation()}>
             <Switch 
                checked={template.meta?.isPublished || false}
                onCheckedChange={(checked) => handlePublishToggle(template.id, checked)}
             />
             <span className="text-xs text-muted-foreground truncate min-w-0">
               {template.meta?.isPublished ? 'Veröffentlicht' : 'Entwurf'}
             </span>
          </div>
        ) : undefined
      }
    };
  }, [handlePublishToggle, canManageTemplateStore, isInternal]);

  const genericItems = useMemo(() => {
    return allTemplates.map(convertToGenericItem);
  }, [allTemplates, convertToGenericItem]);

  const genericActions: GenericItemActions = {
    onItemClick: (id: string) => {
      if (isInternal) {
        navigate(`/post-generator/template-editor?templateId=${id}`);
      } else {
        const template = allTemplates.find(t => t.id === id);
        if (template) {
          openImportDialog(template);
        }
      }
    },
    onEdit: canManageTemplateStore ? (id: string) => {
      const template = allTemplates.find(t => t.id === id);
      if (template) {
        setTemplateToEdit(template);
        setShowEditDialog(true);
      }
    } : undefined,
    onDelete: canManageTemplateStore ? (id: string) => {
      const template = allTemplates.find(t => t.id === id);
      if (template) {
        setTemplateToDelete(template);
        setIsBulkDelete(false);
        setShowDeleteDialog(true);
      }
    } : undefined,
    customActions: [
      !isInternal ? {
        label: 'Importieren',
        icon: <Download className="h-3.5 w-3.5 text-black" />,
        onClick: (id: string) => handleImport(id),
        badge: (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-green-200 text-green-600 bg-green-50">Free</Badge>
        ),
        requiredPermission: Permission.MANAGE_ENTITIES,
        requiredPlan: PlanKey.BASIC,
      } : {
        label: 'Template Editor',
        icon: <PenTool className="h-3.5 w-3.5 text-black" />,
        onClick: (id: string) => {
          navigate(`/post-generator/template-editor?templateId=${id}`);
        }
      },
      canManageTemplateStore ? {
        label: 'Duplizieren',
        icon: <Copy className="h-3.5 w-3.5 text-black" />,
        onClick: (id: string) => handleDuplicate(id),
      } : undefined
    ].filter(Boolean) as any
  };

  return (
    <DataView 
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
      isLoading={isInitialLoading}
      isEmpty={allTemplates.length === 0}
      selectedIds={isInternal ? selectedIds : undefined}
      onMultiSelect={isInternal ? setSelectedIds : undefined}
      onSelectionDelete={isInternal ? handleSelectionDelete : undefined}
    >
      <div data-onboarding="store-templates-header" className="bg-white flex-shrink-0 border-b border-gray-100">
        {state.isInfoBannerVisible && (
          <div className="p-4 pb-0">
            <PageInfoBanner
              title="Template Store"
              description={
                isInternal 
                  ? "Verwalten Sie hier die globalen Templates für alle Kunden." 
                  : "Entdecken Sie professionelle Templates und importieren Sie diese in Ihre Sammlung."
              }
              icon={ShoppingBag}
              variant="purple"
              onClose={() => setInfoBannerVisible(false)}
            />
          </div>
        )}
        
        <DataViewToolbar
          searchQuery={searchTerm}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Store durchsuchen..."
          infoText={`${activeQuery.data?.count || 0} Templates`}
          
          mode="advanced"
          advancedFilterDefs={filterDefs}
          advancedSortDefs={sortDefs}
          advancedState={advancedState}
          onAdvancedStateChange={handleAdvancedStateChange}
          onAdvancedReset={handleAdvancedReset}
          defaultSort={{ key: 'importCount', direction: 'desc' }}
          
          selectedIds={isInternal ? selectedIds : undefined}
          onSelectionDelete={isInternal ? handleSelectionDelete : undefined}
          customBulkActions={isInternal && selectedIds.length > 0 ? [
            {
              label: 'Veröffentlichen',
              icon: <CheckCircle className="h-4 w-4 mr-2" />,
              onClick: () => handleBulkPublish(true)
            },
            {
              label: 'Verstecken',
              icon: <XCircle className="h-4 w-4 mr-2" />,
              onClick: () => handleBulkPublish(false)
            }
          ] : undefined}

          leading={
            canManageTemplateStore ? (
              <CreateTemplateDialog
                isStoreTemplate={true}
                trigger={
                  <PrimaryActionButton 
                    label="Neu"
                    icon={Plus}
                  />
                }
                onSuccess={handleCreateSuccess}
              />
            ) : undefined
          }
        />
      </div>

      <DataViewLayout>
        <div data-onboarding="store-templates-list">
          <DataViewContent
              items={genericItems}
              renderGridItem={(item) => <DataViewGridCard item={item} actions={genericActions} />}
              renderListItem={(item) => <DataViewListCard item={item} actions={genericActions} />}
              loadingIndicator={<DataViewLoadingState />}
              emptyState={
                 <DataViewEmptyState
                   icon={<ShoppingBag className="h-8 w-8 text-gray-400" />}
                   title={searchTerm.trim() !== "" || advancedState.filters.length > 0 ? 'Keine Templates gefunden' : 'Der Store ist leer'}
                   description={searchTerm.trim() !== "" || advancedState.filters.length > 0
                     ? 'Versuchen Sie andere Suchbegriffe oder Filter.'
                     : canManageTemplateStore ? 'Erstellen Sie das erste Store-Template.' : 'Schauen Sie später wieder vorbei.'
                   }
                   action={
                     !(searchTerm.trim() !== "" || advancedState.filters.length > 0) && canManageTemplateStore ? (
                       <CreateTemplateDialog
                         isStoreTemplate={true} 
                         trigger={
                           <PrimaryActionButton 
                             label="Template erstellen"
                             icon={Plus}
                           />
                         }
                         onSuccess={handleCreateSuccess}
                       />
                     ) : undefined
                   }
                 />
              }
          />
        </div>
        <DataViewLoadMore
           onLoadMore={handleLoadMore}
           isLoadingMore={isLoadingMore}
           hasMore={hasMoreTemplates}
        />
      </DataViewLayout>
        
      <TemplateInfoDialog
        open={showInfoDialog}
        onOpenChange={setShowInfoDialog}
        template={selectedTemplate}
        actions={
          !isInternal && selectedTemplate ? (
            <PermissionFeatureWrapper
              requiredPermission={Permission.MANAGE_ENTITIES}
              requiredPlan={PlanKey.BASIC}
            >
              <Button onClick={() => {
                openImportDialog(selectedTemplate);
                setShowInfoDialog(false);
              }}>
                Importieren
              </Button>
            </PermissionFeatureWrapper>
          ) : undefined
        }
      />

      {!isInternal && (
        <ImportStoreTemplateDialog
          open={showImportDialog}
          onOpenChange={handleImportDialogOpenChange}
          template={selectedTemplate}
          onConfirm={handleConfirmImport}
          isSubmitting={importMutation.isPending}
          preventOutsideClose={isOnboardingImportFlow}
        />
      )}

      {templateToEdit && (
        <EditTemplateDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          template={templateToEdit}
          onSuccess={handleEditSuccess}
        />
      )}

      {templateToDelete && !isBulkDelete && (
        <DeleteTemplateDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          templateId={templateToDelete.id}
          templateTitle={templateToDelete.title}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {isBulkDelete && (
        <StandardDeleteDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            title={`${selectedIds.length} Store-Templates löschen`}
            description="Möchten Sie diese Store-Templates wirklich unwiderruflich löschen?"
            itemsList={selectedIds.map(id => {
              const t = allTemplates.find(t => t.id === id);
              return {
                title: t ? t.title : id
              };
            })}
            onDelete={handleBulkDeleteConfirm}
            isLoading={bulkDeleteMutation.isPending}
        />
      )}
    </DataView>
  );
};

export default StoreTemplates;
